/**
 * "Send to" — which people at a supplier receive this project's RFQ emails.
 * Selection is a list of lowercase emails; `null` means everyone (the default).
 */
import React from 'react';
import type { SupplierRfqRecipientCandidate } from '../../utils/supplierRfqRecipients';
import './SupplierRecipientPicker.css';

export interface SupplierRecipientPickerProps {
  candidates: SupplierRfqRecipientCandidate[];
  /** Selected emails; null/undefined = everyone. */
  selected: string[] | null | undefined;
  disabled?: boolean;
  orgName: string;
  onChange: (next: string[]) => void;
}

const SupplierRecipientPicker: React.FC<SupplierRecipientPickerProps> = ({
  candidates,
  selected,
  disabled = false,
  orgName,
  onChange,
}) => {
  const chosen = new Set(selected ?? candidates.map(c => c.email));
  const toggle = (email: string) => {
    const next = new Set(chosen);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    onChange(Array.from(next));
  };
  const noneChosen = candidates.length > 0 && candidates.every(c => !chosen.has(c.email));

  return (
    <div className="srp">
      <span className="srp-label">
        Send to
        {candidates.length > 0 ? (
          <span className="srp-count">
            {candidates.filter(c => chosen.has(c.email)).length} of {candidates.length}
          </span>
        ) : null}
      </span>
      {candidates.length === 0 ? (
        <p className="srp-empty">
          No contacts for {orgName} yet — add an RFQ notifications email or invite a portal user in Vendor
          Management.
        </p>
      ) : (
        <ul className="srp-list" aria-label={`Recipients at ${orgName}`}>
          {candidates.map(c => (
            <li key={c.email}>
              <label className={`srp-row${chosen.has(c.email) ? ' on' : ''}`}>
                <input
                  type="checkbox"
                  checked={chosen.has(c.email)}
                  disabled={disabled}
                  aria-label={`Email ${c.name} (${c.email})`}
                  onChange={() => toggle(c.email)}
                />
                <span className="srp-name">{c.name}</span>
                <span className="srp-detail">{c.detail}</span>
                <span className="srp-email">{c.email}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {noneChosen ? <p className="srp-warn">Nobody is selected — {orgName} will not receive this email.</p> : null}
    </div>
  );
};

export default SupplierRecipientPicker;
